/**
 * CommandPalette — ⌘K / Ctrl-K (and the Search button) fuzzy-jump to any
 * RBAC-permitted destination, INCLUDING the ~20 routes that live in no menu
 * (EXTRA_DESTINATIONS, tagged "hidden"). Results are gated per user by
 * getVisibleDestinations(). Keyboard: ↑/↓ to move, Enter to open, Esc to close;
 * exposed as a modal dialog + listbox, traps Tab on the input, returns focus to
 * the opener on close.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getVisibleDestinations } from '../navigation/navTree';
import { Icon } from './navIcons';

interface CommandPaletteProps {
  user: any;
  open: boolean;
  onClose: () => void;
}

const LISTBOX_ID = 'cmdk-listbox';
const optId = (i: number) => `cmdk-opt-${i}`;

export const CommandPalette: React.FC<CommandPaletteProps> = ({ user, open, onClose }) => {
  const history = useHistory();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const all = useMemo(() => getVisibleDestinations(user), [user]);
  const results = useMemo(() => {
    const query = q.toLowerCase().trim();
    if (!query) return all;
    return all.filter(
      (d) => d.label.toLowerCase().includes(query) || d.crumb.toLowerCase().includes(query) || d.to.includes(query),
    );
  }, [q, all]);

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement as HTMLElement | null;
    setQ('');
    setSel(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(t);
      openerRef.current?.focus?.(); // return focus to the opener on close/unmount
    };
  }, [open]);
  useEffect(() => setSel(0), [q]);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'Tab') e.preventDefault(); // trap focus on the input (rows aren't tabbable)
      else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSel((s) => Math.max(0, Math.min(s + 1, results.length - 1)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSel((s) => Math.max(0, s - 1));
      } else if (e.key === 'Enter') {
        const r = results[sel];
        if (r) {
          history.push(r.to);
          onClose();
        }
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, results, sel, history, onClose]);

  if (!open) return null;

  // getVisibleDestinations always emits every in-nav row before every extra,
  // so this split preserves the same index order as `results` (used by sel/Enter).
  const inNav = results.filter((r) => !r.extra);
  const extra = results.filter((r) => r.extra);
  const go = (to: string) => {
    history.push(to);
    onClose();
  };

  const renderRow = (r: (typeof results)[number], i: number) => (
    <div
      key={r.to}
      id={optId(i)}
      role="option"
      aria-selected={i === sel}
      onClick={() => go(r.to)}
      onMouseEnter={() => setSel(i)}
      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg cursor-pointer ${i === sel ? 'bg-[#323232]' : ''}`}
    >
      <Icon name="bolt" className={`w-4 h-4 shrink-0 ${i === sel ? 'text-primary' : 'text-on-surface-variant'}`} />
      <span className="text-[13.5px] text-white">{r.label}</span>
      {r.extra && (
        <span className="text-[9px] tracking-wider uppercase text-primary border border-primary/20 rounded px-1.5">hidden</span>
      )}
      <span className="ml-auto text-[11px] text-on-surface-variant">{r.crumb}</span>
    </div>
  );

  return (
    <div
      className="scrim z-[150] flex items-start justify-center pt-[14vh]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="w-[min(560px,92vw)] bg-[#242424] border border-gray-700/60 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-800">
          <Icon name="search" strokeWidth={1.6} className="w-4 h-4 text-on-surface-variant" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Jump to anything — even pages not in the nav…"
            aria-label="Search destinations"
            role="combobox"
            aria-expanded
            aria-controls={LISTBOX_ID}
            aria-activedescendant={results.length ? optId(sel) : undefined}
            className="flex-1 bg-transparent outline-none text-[15px] text-white placeholder:text-on-surface-variant"
          />
          <span className="text-[10px] text-on-surface-variant border border-gray-700 rounded px-1.5 py-0.5">esc</span>
        </div>
        <div id={LISTBOX_ID} role="listbox" aria-label="Destinations" className="max-h-[52vh] overflow-y-auto p-2">
          {results.length === 0 && <div className="p-6 text-center text-on-surface-variant text-[13px]">No matches for “{q}”</div>}
          {inNav.length > 0 && (
            <div className="text-[9.5px] tracking-[0.16em] uppercase text-on-surface-variant font-semibold px-3 pt-2.5 pb-1">Destinations</div>
          )}
          {inNav.map((r, i) => renderRow(r, i))}
          {extra.length > 0 && (
            <div className="text-[9.5px] tracking-[0.16em] uppercase text-on-surface-variant font-semibold px-3 pt-2.5 pb-1">
              Not in the nav · reachable only here
            </div>
          )}
          {extra.map((r, i) => renderRow(r, inNav.length + i))}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
